import { useMemo, useState } from 'react';
import { sommerQuotites } from '../../src/partage/quotites.js';
import type { EffetProjete } from './api';

/**
 * COMPOSITEUR D'EFFET (§9 + §14) — la seule voie par laquelle un droit naît.
 *
 * ⚠️ POURQUOI CE COMPOSANT EXISTE. Le formulaire de dépôt envoyait
 * `effetProjete: {}` — TOUJOURS — et l'annonçait en petits caractères :
 * « l'effet projeté n'est pas encore saisissable ». Conséquence, mesurée en
 * lisant l'applicateur : une mutation déposée depuis l'écran, approuvée deux
 * fois, échouait à la SECONDE validation sur « l'effet projeté est vide », et
 * restait bloquée en `attente_validation`. Or `POST /mutations` refuse une
 * seconde mutation tant qu'une autre est en cours : **la parcelle était
 * verrouillée pour toujours.**
 *
 * Le registre était donc bâti, gardé, déployé — et impossible à remplir depuis
 * son propre écran.
 *
 * ⚠️ L'ARITHMÉTIQUE EST IMPORTÉE DU DOMAINE, PAS RECOPIÉE. `sommerQuotites`
 * est le fichier que le service utilise, et dont les essais confrontent le
 * verdict à celui de `fon.garde_quotites` en base. Réécrire ici « la même
 * chose » en produirait une troisième version — et c'est toujours celle qu'on
 * ne teste pas qui diverge.
 */

const TYPES_TITULAIRE = [
  'personne_physique', 'personne_morale', 'etat', 'collectivite',
  'indivision', 'communaute_coutumiere', 'inconnu',
] as const;

const NATURES = [
  'propriete', 'usufruit', 'nue_propriete', 'bail_emphyteotique',
  'occupation_coutumiere', 'concession', 'hypotheque', 'saisie', 'prenotation',
] as const;

export interface LigneTitulaire {
  ref: string; type: string; denomination: string; referenceExterne: string;
}
export interface LigneDroit {
  titulaire: string; nature: string; num: string; den: string; rang: string;
}

export interface EtatEffet {
  titulaires: LigneTitulaire[];
  droits: LigneDroit[];
  radier: boolean;
}

export const effetVide = (): EtatEffet => ({
  titulaires: [{ ref: 'T1', type: 'personne_physique', denomination: '', referenceExterne: '' }],
  droits: [{ titulaire: 'T1', nature: 'propriete', num: '1', den: '1', rang: '' }],
  radier: false,
});

/** Traduit l'état de saisie en `effet_projete`, tel que l'applicateur l'attend. */
export function versEffetProjete(e: EtatEffet): EffetProjete {
  return {
    titulaires: e.titulaires
      .filter((t) => t.denomination.trim().length >= 2)
      .map((t) => ({
        ref: t.ref, type: t.type, denomination: t.denomination.trim(),
        ...(t.referenceExterne.trim() ? { referenceExterne: t.referenceExterne.trim() } : {}),
      })),
    droits: e.droits
      .filter((d) => d.titulaire.trim() && Number(d.num) > 0 && Number(d.den) > 0)
      .map((d) => ({
        titulaire: d.titulaire.trim(), nature: d.nature,
        quotiteNum: Number(d.num), quotiteDen: Number(d.den),
        ...(d.rang.trim() ? { rang: Number(d.rang) } : {}),
      })),
    ...(e.radier ? { radier: 'proprietes_actives' as const } : {}),
  };
}

export interface Exigences {
  droitsAttendus: boolean; radiationAttendue: boolean; note: string;
}

/** Ce qui empêche le dépôt, en clair. Vide ⇒ le dossier peut partir. */
export function obstaclesAuDepot(e: EtatEffet, exigences: Exigences | undefined): string[] {
  const effet = versEffetProjete(e);
  const obstacles: string[] = [];

  if (exigences?.droitsAttendus && effet.droits.length === 0) {
    obstacles.push('Aucun droit à inscrire.');
  }
  if (exigences?.radiationAttendue && !effet.radier) {
    obstacles.push(
      "Rien n'est radié : sans extinction du droit précédent, deux titulaires "
      + 'détiendraient la même parcelle.');
  }
  // Un droit qui cite un repère inexistant : l'applicateur le refuse, mais
  // après la seconde approbation — donc à quelqu'un d'autre, plus tard.
  const refs = new Set(effet.titulaires.map((t) => t.ref));
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  for (const d of effet.droits) {
    if (!refs.has(d.titulaire) && !UUID.test(d.titulaire)) {
      obstacles.push(`Le droit cite « ${d.titulaire} », qui n'est ni un titulaire déclaré ci-dessus ni un identifiant existant.`);
    }
    if (d.quotiteNum > d.quotiteDen) {
      obstacles.push(`Quotité ${d.quotiteNum}/${d.quotiteDen} : une part ne peut pas excéder le tout.`);
    }
  }
  const somme = sommerQuotites(
    effet.droits.filter((d) => d.nature === 'propriete')
      .map((d) => ({ num: d.quotiteNum, den: d.quotiteDen })));
  if (somme.excedentaire) {
    obstacles.push(
      `Les parts de propriété totalisent ${somme.num}/${somme.den} — au-delà du tout. `
      + 'La base refusera cette inscription.');
  }
  return obstacles;
}

export function CompositeurEffet(
  { etat, onChange, exigences }:
  { etat: EtatEffet; onChange: (e: EtatEffet) => void; exigences: Exigences | undefined },
) {
  const [avance, setAvance] = useState(false);
  const effet = useMemo(() => versEffetProjete(etat), [etat]);
  const somme = useMemo(() => sommerQuotites(
    effet.droits.filter((d) => d.nature === 'propriete')
      .map((d) => ({ num: d.quotiteNum, den: d.quotiteDen }))), [effet]);

  const maj = (p: Partial<EtatEffet>) => onChange({ ...etat, ...p });
  const majTitulaire = (i: number, p: Partial<LigneTitulaire>) =>
    maj({ titulaires: etat.titulaires.map((t, k) => (k === i ? { ...t, ...p } : t)) });
  const majDroit = (i: number, p: Partial<LigneDroit>) =>
    maj({ droits: etat.droits.map((d, k) => (k === i ? { ...d, ...p } : d)) });

  return (
    <fieldset className="effet">
      <legend>Effet projeté <span className="aide">ce que la validation ÉCRIRA au registre</span></legend>

      {exigences && <p className="mention">{exigences.note}</p>}

      <h4>Titulaires déclarés</h4>
      <p className="aide">
        Un titulaire déjà connu du registre est réutilisé s'il porte la même référence
        externe — on ne crée pas un doublon à chaque mutation.
      </p>
      {etat.titulaires.map((t, i) => (
        <div className="ligne-effet" key={i}>
          <input
            className="etroit" value={t.ref} aria-label="Repère"
            onChange={(e) => majTitulaire(i, { ref: e.target.value.toUpperCase() })}
            title="Repère local, cité par les droits ci-dessous"
          />
          <select value={t.type} onChange={(e) => majTitulaire(i, { type: e.target.value })}>
            {TYPES_TITULAIRE.map((x) => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}
          </select>
          <input
            value={t.denomination} placeholder="Nom ou raison sociale"
            onChange={(e) => majTitulaire(i, { denomination: e.target.value })}
          />
          <input
            value={t.referenceExterne} placeholder="RCCM, n° d'acte… (facultatif)"
            onChange={(e) => majTitulaire(i, { referenceExterne: e.target.value })}
          />
          <button type="button" className="lien retrait" title="Retirer"
                  onClick={() => maj({ titulaires: etat.titulaires.filter((_, k) => k !== i) })}>×</button>
        </div>
      ))}
      <button type="button" className="bouton discret" onClick={() => maj({
        titulaires: [...etat.titulaires, {
          ref: `T${etat.titulaires.length + 1}`, type: 'personne_physique',
          denomination: '', referenceExterne: '',
        }],
      })}>Ajouter un titulaire</button>

      <h4>Droits à inscrire</h4>
      {etat.droits.map((d, i) => (
        <div className="ligne-effet" key={i}>
          <input
            className="etroit" value={d.titulaire} aria-label="Repère du titulaire"
            onChange={(e) => majDroit(i, { titulaire: e.target.value })}
          />
          <select value={d.nature} onChange={(e) => majDroit(i, { nature: e.target.value })}>
            {NATURES.map((n) => <option key={n} value={n}>{n.replace(/_/g, ' ')}</option>)}
          </select>
          {/* ⚠️ DEUX ENTIERS, PAS UN POURCENTAGE. Trois héritiers à parts égales,
              c'est 1/3 chacun ; en décimal, 0,333 × 3 ne fait pas 1 et la garde
              rejetterait une succession parfaitement valide. */}
          <span className="fraction">
            <input className="etroit" type="number" min={1} value={d.num}
                   onChange={(e) => majDroit(i, { num: e.target.value })} aria-label="Numérateur" />
            <span>/</span>
            <input className="etroit" type="number" min={1} value={d.den}
                   onChange={(e) => majDroit(i, { den: e.target.value })} aria-label="Dénominateur" />
          </span>
          {(d.nature === 'hypotheque' || d.nature === 'saisie') && (
            <input className="etroit" type="number" min={1} value={d.rang} placeholder="rang"
                   onChange={(e) => majDroit(i, { rang: e.target.value })} aria-label="Rang" />
          )}
          <button type="button" className="lien retrait" title="Retirer"
                  onClick={() => maj({ droits: etat.droits.filter((_, k) => k !== i) })}>×</button>
        </div>
      ))}
      <button type="button" className="bouton discret" onClick={() => maj({
        droits: [...etat.droits, {
          titulaire: etat.titulaires[0]?.ref ?? 'T1', nature: 'propriete',
          num: '1', den: '1', rang: '',
        }],
      })}>Ajouter un droit</button>

      {/* La somme, en fractions exactes, calculée par le MÊME code que le service. */}
      {somme.parts > 0 && (
        <p className={somme.excedentaire ? 'erreur' : somme.complet ? 'succes' : 'mention'}>
          Parts de propriété : <strong>{somme.num}/{somme.den}</strong>
          {somme.excedentaire
            ? ' — au-delà du tout, la base refusera.'
            : somme.complet
              ? ' — le fonds est entièrement attribué.'
              : ` — il resterait ${somme.reste?.num}/${somme.reste?.den} non attribué.`}
        </p>
      )}

      <label className="case">
        <input type="checkbox" checked={etat.radier}
               onChange={(e) => maj({ radier: e.target.checked })} />
        <span>Radier les propriétés actives de cette parcelle</span>
        <em className="aide">
          Obligatoire pour une vente, une donation, une succession ou un échange :
          sans cela, l'ancien et le nouveau titulaire coexisteraient.
        </em>
      </label>

      <details open={avance} onToggle={(e) => setAvance((e.target as HTMLDetailsElement).open)}>
        <summary>Voir l'effet tel qu'il sera enregistré</summary>
        {/* Montrer le JSON n'est pas de la coquetterie : c'est CE contenu que la
            validation lira, et un agent doit pouvoir le relire avant de déposer. */}
        <pre className="brut">{JSON.stringify(effet, null, 2)}</pre>
      </details>
    </fieldset>
  );
}
